import {useEffect, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {useForecastFetchQuery} from '../../forecast-app/shared/hooks/useForecastFetchQuery';
import {parseReportTaxonomy, type ReportTaxonomy} from './contracts/report-taxonomy.types';
import type {useReportTaxonomyQuery$data} from './__generated__/useReportTaxonomyQuery.graphql';

const TASK_REPORT_TYPE = 'TASK_REPORT';

const reportTaxonomyQuery = graphql`
	query useReportTaxonomyQuery($reportType: String) {
		viewer {
			reportTaxonomy(reportType: $reportType)
		}
	}
`;

/** Resolves the task-report taxonomy the conditions editor needs, independent of the ephemeral
 * chat handoff. Seeds from `seed` — the taxonomy the chat surface may have passed through router
 * state — so editing works immediately when it is present, and ALWAYS fires its own fetch on mount
 * so a missing, stale, or failed handoff still populates it (that gap left the "add condition"
 * picker hidden whenever the surface was reached without a resolved taxonomy in tow). Returns null
 * until a usable taxonomy is available; a malformed or empty-field-set response leaves the current
 * value unchanged so the editor stays gated rather than rendering an unusable picker. */
export function useReportTaxonomy(seed?: ReportTaxonomy | null): ReportTaxonomy | null {
	const [taxonomy, setTaxonomy] = useState<ReportTaxonomy | null>(seed ?? null);
	const {data, fetch} = useForecastFetchQuery<useReportTaxonomyQuery$data, {reportType: string}>(reportTaxonomyQuery);

	// Fire exactly once on mount. `fetch` is a fresh closure every render (the hook does not
	// memoise it), so a plain [fetch] dependency would refetch on every render — the ref guard
	// pins it to the first mount while keeping the dependency honest for the linter.
	const fetchedRef = useRef(false);
	useEffect(() => {
		if (fetchedRef.current) {
			return;
		}
		fetchedRef.current = true;
		fetch({reportType: TASK_REPORT_TYPE});
	}, [fetch]);

	// The fetch hook signals completion via its returned `data`, not a callback argument, so react
	// to `data` here. Parse defensively: a malformed/empty JSON string must not crash the surface,
	// it should leave the taxonomy on its current value (seed or null).
	useEffect(() => {
		const raw = data?.viewer?.reportTaxonomy;
		if (!raw) {
			return;
		}
		try {
			const parsed = parseReportTaxonomy(raw);
			if (parsed.fields && parsed.fields.length > 0) {
				setTaxonomy(parsed);
			}
		} catch {
			// leave taxonomy as-is — condition editing stays gated
		}
	}, [data]);

	return taxonomy;
}
